import { useCallback, useEffect, useRef, useState } from 'react'
import debug from 'debug'
import { useInactiveTab } from './inactive-helper'
import Cookies from 'js-cookie'

export interface PromptClickHandlerProps {
    promptId: string
    type?: 'client' | 'local-storage' | 'cookies'
    onOutsideClick?: () => void
    onPromptHide?: () => void
    onPromptShow?: () => void
    defaultHiddenState?: boolean
    disableOutsideClick?: boolean
    showPromptAfterInactivityOfMs?: number
    hidePromptAfterInactivityOfMs?: number
    cookieExpiryInDays?: number
    debug?: debug.Debugger
}

const shouldDisplayFromLocalStorage = (
    promptId: string,
    debug?: debug.Debugger,
) => {
    if (
        typeof window === 'undefined' ||
        typeof window.localStorage === 'undefined'
    ) {
        debugPrompt(
            'Window or localStorage is undefined, cannot access localStorage.',
            debug,
        )
        return
    }

    const item = window.localStorage.getItem(promptId)

    debugPrompt('Checking localStorage for prompt display:', debug, { item })

    if (item !== null) {
        return item === 'true'
    }
}
const setPromptShownInLocalStorage = (
    promptId: string,
    shown: boolean,
    debug?: debug.Debugger,
) => {
    if (typeof window !== 'undefined' && window.localStorage) {
        debugPrompt(
            'Setting localStorage for prompt display: ' + promptId,
            debug,
            {
                shown,
            },
        )
        window.localStorage.setItem(promptId, shown ? 'true' : 'false')
    } else {
        debugPrompt(
            'Window or localStorage is undefined, cannot set localStorage.',
            debug,
        )
    }
}
const shouldDisplayFromCookie = (promptId: string, debug?: debug.Debugger) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        debugPrompt(
            'Window or document is undefined, cannot access cookies.',
            debug,
        )
        return
    }

    const cookie = Cookies.get(promptId)

    debugPrompt('Checking cookie for prompt display:', debug, { cookie })

    if (cookie !== undefined) {
        return cookie === 'true'
    }
}
const setPromptShownInCookie = (
    promptId: string,
    shown: boolean,
    expiryInDays = 1,
    debug?: debug.Debugger,
) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        debugPrompt(
            'Window or document is undefined, cannot set cookies.',
            debug,
        )
        return
    }

    debugPrompt('Setting cookie for prompt display: ' + promptId, debug, {
        shown,
    })
    Cookies.set(promptId, shown ? 'true' : 'false', {
        expires: expiryInDays,
        path: '/',
    })
}

export const usePromptClickHandler = ({
    promptId,
    type = 'client',
    onOutsideClick,
    onPromptHide,
    onPromptShow,
    defaultHiddenState = true,
    disableOutsideClick = false,
    showPromptAfterInactivityOfMs = 0,
    hidePromptAfterInactivityOfMs = 0,
    cookieExpiryInDays = 1,
    debug,
}: PromptClickHandlerProps) => {
    const previousHiddenState =
        type === 'local-storage'
            ? shouldDisplayFromLocalStorage(promptId, debug)
            : type === 'cookies'
            ? shouldDisplayFromCookie(promptId, debug)
            : undefined

    debugPrompt(
        'Initializing prompt click handler with the following settings:',
        debug,
        { previousHiddenState, defaultHiddenState },
    )

    const [hidden, setHidden] = useState(
        previousHiddenState !== undefined
            ? !previousHiddenState
            : defaultHiddenState,
    )
    const backgroundComponentRef = useRef<HTMLDivElement | null>(null)

    const hide = useCallback(() => {
        setHidden(true)

        // Depending on the type, set the prompt as shown in the appropriate storage mechanism so that it can be persisted across sessions if needed
        switch (type) {
            case 'local-storage': {
                setPromptShownInLocalStorage(promptId, false, debug)
                break
            }
            case 'cookies': {
                setPromptShownInCookie(
                    promptId,
                    false,
                    cookieExpiryInDays,
                    debug,
                )
                break
            }
        }

        onPromptHide?.()
        debugPrompt('Prompt has become hidden.', debug)
    }, [type, onPromptHide, debug, promptId, cookieExpiryInDays])

    const show = useCallback(() => {
        setHidden(false)

        // Depending on the type, set the prompt as shown in the appropriate storage mechanism so that it can be persisted across sessions if needed
        switch (type) {
            case 'local-storage': {
                setPromptShownInLocalStorage(promptId, true, debug)
                break
            }
            case 'cookies': {
                setPromptShownInCookie(
                    promptId,
                    true,
                    cookieExpiryInDays,
                    debug,
                )
                break
            }
        }

        onPromptShow?.()
        debugPrompt('Prompt has become visible.', debug)
    }, [type, onPromptShow, debug, promptId, cookieExpiryInDays])

    // Hide the prompt on keyboard escape press
    const hideOnEscape = useCallback(
        (e: KeyboardEvent) => {
            if (disableOutsideClick || e.key !== 'Escape') {
                return
            }

            debugPrompt('Escape key pressed, hiding prompt.', debug)
            hide()
        },
        [hide, disableOutsideClick, debug],
    )
    // Hide the prompt on outside click
    const hideOnOutsideClick = useCallback(
        (ev: MouseEvent) => {
            if (
                disableOutsideClick ||
                !backgroundComponentRef.current ||
                ev.target !== backgroundComponentRef.current
            ) {
                return
            }

            debugPrompt('Outside click detected, hiding prompt.', debug)
            hide()
            onOutsideClick?.()
        },
        [hide, disableOutsideClick, onOutsideClick, debug],
    )

    const shouldShowAfterInactivity =
        showPromptAfterInactivityOfMs > 0 && previousHiddenState !== false
    const shouldHideAfterInactivity = hidePromptAfterInactivityOfMs > 0

    // If either of the inactivity timeouts are set, use the hook to handle showing/hiding the prompt after inactivity
    useInactiveTab({
        timeout: shouldShowAfterInactivity
            ? showPromptAfterInactivityOfMs
            : shouldHideAfterInactivity
            ? hidePromptAfterInactivityOfMs
            : undefined,
        onInactive: () => {
            if (shouldShowAfterInactivity) {
                // If it's already shown, don't show it again
                if (!hidden) {
                    return
                }

                debugPrompt(
                    `User has been inactive for ${showPromptAfterInactivityOfMs}ms, showing prompt.`,
                    debug,
                )
                show()
            } else if (shouldHideAfterInactivity) {
                // If it's already hidden, don't hide it again
                if (hidden) {
                    return
                }

                debugPrompt(
                    `User has been inactive for ${hidePromptAfterInactivityOfMs}ms, hiding prompt.`,
                    debug,
                )
                hide()
            }
        },
    })

    useEffect(() => {
        const cleanup = () => {
            document.body.removeEventListener('keyup', hideOnEscape)

            if (backgroundComponentRef.current) {
                backgroundComponentRef.current.removeEventListener(
                    'click',
                    hideOnOutsideClick,
                )
            }
        }

        // If it's being displayed, listen for escape key up
        if (!hidden) {
            document.body.addEventListener('keyup', hideOnEscape)

            if (backgroundComponentRef.current) {
                backgroundComponentRef.current.addEventListener(
                    'click',
                    hideOnOutsideClick,
                )
            }
        } else {
            cleanup()
        }

        return cleanup
    }, [hidden, hideOnEscape, hideOnOutsideClick, backgroundComponentRef])

    return {
        isHidden: hidden,
        hide,
        show,
        backgroundComponentRef,
    }
}

const debugPrompt = (
    message: string,
    debug?: debug.Debugger,
    ...args: any[]
) => {
    if (debug !== undefined) {
        debug(message, args)
    }
}
